import { Table, Body, OpenNewWindowIcon, Header } from './styled';
import { shallowEqual, useSelector } from 'react-redux';
import { useParagrapher } from '@/hooks/useParagrapher';
import ContentLoader from 'react-content-loader';
import { useTheme } from '@simplywallst/ui-core';
import Media from 'react-media';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import selector from './selector';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';
import { useSummaryV2 } from '@/hooks/useCompany';

interface AboutTheCompanyContentProps {
  expanded?: boolean;
}

function AboutTheCompanyContent({
  expanded = true,
}: AboutTheCompanyContentProps) {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useUserAgent();

  const state = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const { website, longDescription } = useSummaryV2();
  const paragraphs = useParagrapher(longDescription);

  const websiteLink =
    typeof website === 'string' ? (
      <a
        href={`https://${website}`}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {website}
        <OpenNewWindowIcon />
      </a>
    ) : (
      'n/a'
    );

  const t = useOverviewTranslation();

  return (
    <>
      <Header>{t('about.heading')}</Header>
      <Table>
        <thead>
          <tr>
            <th>{t('about.table.header.founded')}</th>
            <th>{t('about.table.header.employees')}</th>
            <th>{t('about.table.header.ceo')}</th>
            <Media
              query={{ minWidth: theme.smMinRaw }}
              defaultMatches={isDesktop}
              render={() => <th>{t('about.table.header.website')}</th>}
            />
          </tr>
        </thead>
        <tbody>
          {typeof state === 'undefined' ? (
            <tr>
              {Array.from({ length: 4 }).map((_, i) => (
                <td key={i}>
                  <ContentLoader
                    foregroundColor={theme.color.spaceGray}
                    backgroundColor={theme.color.darkGray}
                    height="14"
                    width="100"
                  >
                    <rect x="0" y="0" rx="1" ry="1" width="100" height="14" />
                  </ContentLoader>
                </td>
              ))}
            </tr>
          ) : (
            <tr>
              <td>{state.founded}</td>
              <td>{state.employees}</td>
              <td>{state.ceo}</td>
              <Media
                query={{ minWidth: theme.smMinRaw }}
                defaultMatches={isDesktop}
                render={() => <td>{websiteLink}</td>}
              />
            </tr>
          )}
        </tbody>
      </Table>
      <Media
        query={{ maxWidth: theme.xsMaxRaw }}
        defaultMatches={isMobile || isTablet}
        render={() => (
          <Table>
            <thead>
              <tr>
                <th>{t('about.table.header.website')}</th>
              </tr>
            </thead>
            <tbody>
              {typeof state === 'undefined' ? (
                <tr>
                  <td>
                    <ContentLoader
                      foregroundColor={theme.color.spaceGray}
                      backgroundColor={theme.color.darkGray}
                      height="14"
                      width="100"
                    >
                      <rect x="0" y="0" rx="1" ry="1" width="100" height="14" />
                    </ContentLoader>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td>{websiteLink}</td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      />

      {paragraphs.map((paragraph, index) => {
        if (expanded) {
          return <Body key={index}>{paragraph}</Body>;
        } else {
          if (index === 0) {
            return (
              <Body key={index} clamped>
                {paragraph}
              </Body>
            );
          } else {
            return null;
          }
        }
      })}
    </>
  );
}

export default AboutTheCompanyContent;
